import {
  FunctionComponent,
  KeyboardEvent,
  PointerEvent,
  WheelEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

import { API_URL } from "../../config";
import useFiles from "../../hooks/useFiles";
import {
  applyReviewKeyPan,
  buildReviewPanZoomTransform,
  createFitReviewViewport,
  getJourneyLogReviewFileKind,
  panReviewViewport,
  rotateReviewViewport,
  selectJourneyLogReviewFiles,
  zoomInReview,
  zoomOutReview,
  type ReviewViewport,
} from "./journeyLogReviewUtils";

export const buildFlightFileUrl = (flightId: string, fileName: string) =>
  `${API_URL}/flights/${encodeURIComponent(flightId)}/files/${encodeURIComponent(fileName)}`;

type DocumentViewerProps = {
  files: string[];
  selectedFile: string | null;
  documentUrl: string | null;
  isLoading: boolean;
  errorMessage?: string | null;
  downloadHref?: string | null;
  onSelectFile: (fileName: string) => void;
  onRetry?: () => void;
};

export const LeonJourneyLogDocumentViewer: FunctionComponent<DocumentViewerProps> = ({
  files,
  selectedFile,
  documentUrl,
  isLoading,
  errorMessage,
  downloadHref,
  onSelectFile,
  onRetry,
}) => {
  const [viewport, setViewport] = useState<ReviewViewport>(createFitReviewViewport);
  const [isPanning, setIsPanning] = useState(false);
  const lastPointer = useRef<{ x: number; y: number } | null>(null);
  const selectedIndex = selectedFile ? files.indexOf(selectedFile) : -1;
  const fileKind = selectedFile ? getJourneyLogReviewFileKind(selectedFile) : null;
  const canSwitch = files.length > 1;

  useEffect(() => {
    setViewport(createFitReviewViewport());
  }, [selectedFile, documentUrl]);

  const selectByOffset = (offset: number) => {
    if (!canSwitch || selectedIndex < 0) return;
    const nextIndex = (selectedIndex + offset + files.length) % files.length;
    onSelectFile(files[nextIndex]);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    lastPointer.current = { x: event.clientX, y: event.clientY };
    setIsPanning(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!lastPointer.current) return;
    const dx = event.clientX - lastPointer.current.x;
    const dy = event.clientY - lastPointer.current.y;
    lastPointer.current = { x: event.clientX, y: event.clientY };
    setViewport((current) => panReviewViewport(current, dx, dy));
  };

  const endPan = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    lastPointer.current = null;
    setIsPanning(false);
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setViewport((current) => ({
      ...current,
      scale: event.deltaY < 0 ? zoomInReview(current.scale) : zoomOutReview(current.scale),
    }));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = applyReviewKeyPan(viewport, event.key);
    if (!next) return;
    event.preventDefault();
    setViewport(next);
  };

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }} data-testid="jl-review-pane" data-review-files={files.join("|")}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
      >
        {canSwitch ? (
          <>
            <IconButton
              size="small"
              aria-label="Previous journey log document"
              onClick={() => selectByOffset(-1)}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <TextField
              select
              size="small"
              label="Journey log document"
              value={selectedFile || ""}
              onChange={(event) => onSelectFile(event.target.value)}
              sx={{ minWidth: 220, flex: 1 }}
              inputProps={{ "data-testid": "jl-review-file-select" }}
            >
              {files.map((fileName) => {
                const kind = getJourneyLogReviewFileKind(fileName);
                return (
                  <MenuItem key={fileName} value={fileName}>
                    {fileName}
                    {kind === "pdf" ? " (PDF)" : " (Photo)"}
                  </MenuItem>
                );
              })}
            </TextField>
            <IconButton
              size="small"
              aria-label="Next journey log document"
              onClick={() => selectByOffset(1)}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </>
        ) : selectedFile ? (
          <Typography variant="body2" sx={{ flex: 1, overflowWrap: "anywhere" }}>
            {selectedFile}
          </Typography>
        ) : null}

        <Tooltip title="Zoom out">
          <IconButton
            size="small"
            aria-label="Zoom out"
            onClick={() =>
              setViewport((current) => ({ ...current, scale: zoomOutReview(current.scale) }))
            }
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom in">
          <IconButton
            size="small"
            aria-label="Zoom in"
            onClick={() =>
              setViewport((current) => ({ ...current, scale: zoomInReview(current.scale) }))
            }
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit">
          <IconButton
            size="small"
            aria-label="Reset view"
            onClick={() => setViewport(createFitReviewViewport())}
          >
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {fileKind === "image" ? (
          <>
            <Tooltip title="Rotate left">
              <IconButton
                size="small"
                aria-label="Rotate photo left"
                onClick={() => setViewport((current) => rotateReviewViewport(current, -90))}
              >
                <RotateLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rotate right">
              <IconButton
                size="small"
                aria-label="Rotate photo right"
                onClick={() => setViewport((current) => rotateReviewViewport(current, 90))}
              >
                <RotateRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : null}
        {downloadHref ? (
          <>
            <Tooltip title="Open in new tab">
              <IconButton
                size="small"
                aria-label="Open journey log document in new tab"
                href={downloadHref}
                target="_blank"
                rel="noreferrer"
                component="a"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                size="small"
                aria-label="Download journey log document"
                href={downloadHref}
                component="a"
              >
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : null}
      </Stack>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : errorMessage ? (
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Button
                color="inherit"
                size="small"
                aria-label="Retry loading journey log document"
                onClick={onRetry}
              >
                Retry
              </Button>
            ) : undefined
          }
        >
          {errorMessage}
        </Alert>
      ) : !selectedFile ? (
        <Alert severity="info">No journey log document uploaded</Alert>
      ) : (
        <Box
          data-testid="jl-review-viewport"
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endPan}
          onPointerCancel={endPan}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
          sx={{
            position: "relative",
            overflow: "hidden",
            flex: 1,
            minHeight: { xs: 280, md: 520 },
            border: 1,
            borderColor: "divider",
            borderRadius: 1.5,
            bgcolor: "background.default",
            cursor: isPanning ? "grabbing" : "grab",
            outline: "none",
          }}
        >
          <Box
            data-testid="jl-review-stage"
            data-transform={buildReviewPanZoomTransform(viewport)}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: buildReviewPanZoomTransform(viewport),
              transformOrigin: "center center",
              pointerEvents: "none",
            }}
          >
            {documentUrl && fileKind === "pdf" ? (
              <Box
                component="iframe"
                title={selectedFile}
                src={documentUrl}
                sx={{ width: "100%", height: "100%", border: 0, bgcolor: "background.paper" }}
              />
            ) : null}
            {documentUrl && fileKind === "image" ? (
              <Box
                component="img"
                src={documentUrl}
                alt={selectedFile}
                data-rotation={`${viewport.rotation}`}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  userSelect: "none",
                  transform: `rotate(${viewport.rotation}deg)`,
                  transformOrigin: "center center",
                }}
              />
            ) : null}
          </Box>
        </Box>
      )}
    </Stack>
  );
};

type ReviewPaneProps = {
  flightId: string;
};

export const LeonJourneyLogReviewPane: FunctionComponent<ReviewPaneProps> = ({
  flightId,
}) => {
  const { data, isLoading, isError, error, refetch } = useFiles(flightId);
  const files = useMemo(() => selectJourneyLogReviewFiles(data ?? []), [data]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  const defaultFile = files.length ? files[files.length - 1] : null;
  const activeFile =
    selectedFile && files.includes(selectedFile) ? selectedFile : defaultFile;

  useEffect(() => {
    if (activeFile && selectedFile !== activeFile) {
      setSelectedFile(activeFile);
    }
    if (!activeFile && selectedFile) {
      setSelectedFile(null);
    }
  }, [activeFile, selectedFile]);

  useEffect(() => {
    if (!activeFile) {
      setDocumentUrl(null);
      setIsDocumentLoading(false);
      setDocumentError("");
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();

    const loadDocument = async () => {
      setIsDocumentLoading(true);
      setDocumentError("");
      setDocumentUrl(null);
      try {
        const response = await fetch(buildFlightFileUrl(flightId, activeFile), {
          credentials: "include",
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error("Failed to load journey log document");
        }
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setDocumentUrl(objectUrl);
      } catch (loadError) {
        if (cancelled || (loadError instanceof Error && loadError.name === "AbortError")) {
          return;
        }
        setDocumentError(
          loadError instanceof Error ? loadError.message : "Failed to load journey log document",
        );
      } finally {
        if (!cancelled) {
          setIsDocumentLoading(false);
        }
      }
    };

    void loadDocument();

    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [activeFile, flightId, reloadToken]);

  const errorMessage = isError
    ? error?.message || "Failed to load flight files"
    : documentError || null;

  return (
    <LeonJourneyLogDocumentViewer
      files={files}
      selectedFile={activeFile}
      documentUrl={documentUrl}
      isLoading={isLoading || isDocumentLoading}
      errorMessage={errorMessage}
      downloadHref={activeFile ? buildFlightFileUrl(flightId, activeFile) : null}
      onSelectFile={setSelectedFile}
      onRetry={() => {
        void refetch();
        setReloadToken((current) => current + 1);
      }}
    />
  );
};
